import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import { useFormik } from 'formik';
import { purchaseOrderSchema, TAB_ERROR_PREFIXES } from './schemas/indexSchema';
import { client } from '../../../../util';
import type {
  PurchaseOrder,
  PurchaseOrderCreateInput,
  OrderflowSettings,
  PaymentTerms,
  ShipMethods,
  TaxCode,
} from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import {
  setDisableButtons,
  closeModal,
  fetchPurchaseOrderToEdit,
  PurchaseOrderModalMode,
  setFormData,
  setLoading,
  setMode,
} from '../../../../state/purchaseOrderModalSlice';
import DraggablePaper from '../../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { serverLatencyMessage } from '../../constants/messages';
import Items from './Items';
import dayjs from 'dayjs';
import VendorSlipIcon from '../../../Icons/VendorSlipIcon';
import { faIndustry } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getTabsWithVisibleErrors,
  tabErrorSx,
  tabHasError,
  tabIndicatorErrorSx,
  useFormikTabErrorMiddleware,
} from '../../../utils/FormikTabErorrs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function CreateEditPurchaseOrderModal() {
  const theme = useTheme();
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tAddO } = useTranslation([], {
    keyPrefix: 'team.Modals.Purchase Orders.createAndEdit.Add Purchase Order',
  });

  const {
    mode,
    stateIsChanged,
    formData,
    isOpenPurchaseOrderModal,
    purchaseOrderIdToEdit,
    updateCallback,
    createCallback,
    loading,
    disableButtons,
  } = useSelector(({ purchaseOrderModalSlice }) => purchaseOrderModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const [tab, setTab] = useState(0);

  const dispatch = useDispatch();

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms[]>([]);
  const [shipMethods, setShipMethods] = useState<ShipMethods[]>([]);
  const [purchaseOrderflowSettings, setOrderflowSettings] =
    useState<OrderflowSettings | null>(null);

  const errorTabTrigger = (tabsWithError: number[]) => {
    if (tabsWithError.length && tabsWithError[0] !== undefined)
      setTab(tabsWithError[0]);
  };

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const updateDisableButtons = (value: boolean) => {
    dispatch(setDisableButtons(value));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    if (updateCallback) updateCallback(undefined);
    if (createCallback) createCallback(undefined);
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  const exitViewMode = async () => {
    dispatch(setMode(PurchaseOrderModalMode.EDIT));
  };

  const getSuggestedCode = async () => {
    const code = await client.getSuggestedPurchaseOrderCode.query();
    formik.setFieldValue('code', code);
  };
  const checkPurchaseOrderCode = async (code: string) => {
    const number = Number(code);
    const resp = await client.getCounters.query();
    if (number < resp.purchaseOrderNumber) {
      formik.setFieldError(
        'code',
        `Must be more than ${resp.purchaseOrderNumber - 1}`,
      );
      return false;
    }
    return true;
  };

  const handleSave = async (payload: PurchaseOrderCreateInput) => {
    updateLoading(true);
    if (mode == PurchaseOrderModalMode.EDIT) {
      // check if modal is in edit mode
      if (purchaseOrderIdToEdit) {
        // double check if id exists. Should never happen.
        try {
          const updatedPurchaseOrder = await client.updatePurchaseOrder.mutate({
            ...payload,
            rate: payload.rate / 100,
            PurchaseOrderId: purchaseOrderIdToEdit,
            orderDate: payload.orderDate ?? 0,
            estimatedShipDate: payload.estimatedShipDate ?? 0,
            deadline: payload.deadline ?? 0,
            items: payload.items.map((it: any) => {
              const { loading, primaryVendor, ...rest } = it;

              return {
                ...rest,
                // set VendorId (or null if vendor not set)
                primaryVendor: primaryVendor ? primaryVendor.VendorId : '',
              };
            }),
          });
          if (updateCallback) updateCallback(updatedPurchaseOrder);
          dispatch(
            pushToast({
              message: `Purchase Order ${updatedPurchaseOrder?.code || ''} updated`,
              additionalInfo: serverLatencyMessage,
              type: 'success',
              duration: 7000,
            }),
          );
        } catch (error) {
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
          updateLoading(false);
          return;
        }
      } else {
        // this should never happaned but its good to have this
        console.log(
          'ERROR: Purchase Order id is wrong or missing: ',
          purchaseOrderIdToEdit,
        );
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } else {
      const isValid = await checkPurchaseOrderCode(payload.code);
      if (!isValid) {
        updateLoading(false);
        setTab(0);
        return;
      }
      try {
        if (team === 'init' || team === 'loading') return;
        const newPurchaseOrder: PurchaseOrder =
          await client.createPurchaseOrder.mutate({
            ...payload,
            rate: payload.rate / 100,
            items: payload.items.map((it: any) => {
              const { loading, primaryVendor, ...rest } = it;

              return {
                ...rest,
                // set VendorId (or null if vendor not set)
                primaryVendor: primaryVendor ? primaryVendor.VendorId : '',
              };
            }),
          });
        if (createCallback) createCallback(newPurchaseOrder);
        dispatch(
          pushToast({
            message: `Purchase Order ${newPurchaseOrder?.code || ''} created`,
            additionalInfo: serverLatencyMessage,
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
        updateLoading(false);
        return;
      }
    }
    handleClose();
    updateLoading(false);
  };

  // Use formData from redux as initialValues
  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: false, // Do not Reinitialize form values when formData in redux changes
    validationSchema: purchaseOrderSchema,
    onSubmit: handleSave,
  });
  useFormikTabErrorMiddleware(formik, TAB_ERROR_PREFIXES, errorTabTrigger);

  // Sync formik values back to Redux when they change
  useEffect(() => {
    dispatch(setFormData(formik.values));
  }, [formik.values]);

  const fetchOptions = async () => {
    //HEREE
    try {
      const [shipMethods, paymentTerms, settings] = await Promise.all([
        client.allShippingMethods.query(),
        client.allPaymentTerms.query(),
        client.getSettings.query(),
      ]);
      setShipMethods(shipMethods.results);
      setPaymentTerms(paymentTerms.results);
      setOrderflowSettings(settings);
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
  };

  // After fetching purchaseOrder when editing:

  const fetchDataForEdit = async () => {
    if (purchaseOrderIdToEdit) {
      try {
        updateLoading(true);
        await fetchOptions();
        const purchaseOrder = await dispatch(
          fetchPurchaseOrderToEdit(purchaseOrderIdToEdit),
        );
        // this is to make items vendor field into object strait ahead so we show user that we are loading vendors
        let items = formik.values.items;
        if (!items || items.length === 0) {
          items = (purchaseOrder.items || []).map((it: any) => {
            if (it.primaryVendor) {
              // backend gave us a vendorId string -> wrap it
              return {
                ...it,
                primaryVendor: {
                  VendorId: it.primaryVendor,
                  loadingV: true,
                },
                loadingV: true,
              };
            } else {
              // no vendor on this row -> normalize to null
              return {
                ...it,
                primaryVendor: null,
                loadingV: false,
              };
            }
          });
        }
        // Once we have the purchaseOrder, set it into the formData
        formik.setValues({
          ...formik.values, // Keeping any other existing form values
          ...purchaseOrder, // Seting vendor data returned from API
          rate: (purchaseOrder.rate || 0) * 100, // converting to percentage for UI
          items, // Items are set with loading vendors.
        });
        updateLoading(false);
        // Here we loaded main data so we can show to user but items are loading so we are disabling buttons
        updateDisableButtons(true);

        // filter items that have primaryVendor needing full data fetch (AKA not null)
        const rowsNeedingVendor = items.filter(
          (row: any) =>
            row.loadingV === true &&
            row.primaryVendor &&
            row.primaryVendor.VendorId,
        );

        if (rowsNeedingVendor.length === 0) {
          // nothing to resolve, done
          return;
        }

        // We'll keep a mutable copy of items and push full array into Formik
        let workingItems = [...items];

        // helper to apply updates for ONE row, then sync Formik
        const applyRowUpdate = (rowItemId: string, patch: any) => {
          workingItems = workingItems.map((existing: any) =>
            existing.itemId === rowItemId
              ? { ...existing, ...patch }
              : existing,
          );
          formik.setFieldValue('items', workingItems);
        };

        // run all vendor fetches in parallel
        if (rowsNeedingVendor.length > 0) {
          await Promise.all(
            rowsNeedingVendor.map(async (row: any) => {
              try {
                // row.primaryVendor.VendorId is the UUID string we wrapped
                const vendorData = await client.vendorById.query(
                  row.primaryVendor.VendorId as string,
                );

                // success -> replace placeholder {VendorId,loading:true}
                // with full vendor object + loading:false
                applyRowUpdate(row.itemId, {
                  primaryVendor: vendorData,
                  loadingV: false,
                });
              } catch (error: any) {
                if (error instanceof TRPCClientError) {
                  dispatch(
                    pushToast({ message: error.message, type: 'error' }),
                  );
                } else {
                  dispatch(pushToast({ message: serverError, type: 'error' }));
                }
                applyRowUpdate(row.itemId, {
                  primaryVendor: null,
                  loadingV: false,
                });
              }
            }),
          );
        }
      } catch (error) {
        handleClose();

        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
      } finally {
        updateDisableButtons(false);
        updateLoading(false);
      }
    }
  };

  useEffect(() => {
    // Here we don't make api calls if modal sate not changed (came back from other modal)
    // We also need to chek if its in edit mode because the state will be changed in it but we still need to fetch
    if (!isOpenPurchaseOrderModal) return;
    if (stateIsChanged) return;
    const fetchData = async () => {
      updateLoading(true);
      await Promise.all([fetchOptions(), getSuggestedCode()]);
      if (purchaseOrderIdToEdit) {
        await Promise.all([fetchDataForEdit()]);
      }
      updateLoading(false);
    };
    // Getting Initial data from formData (redux)
    formik.setValues(formData);
    fetchData();
  }, [isOpenPurchaseOrderModal]);

  const renderTitle = () => {
    const titleInfo = (
      <>
        {formik.values.code && (
          <Box component="span">
            <Box component="span" sx={{ color: 'text.secondary' }}>
              #
            </Box>
            <Box component="span" sx={{ color: 'primary.main' }}>
              {formik.values.code}
            </Box>
          </Box>
        )}{' '}
        {formik.values.vendorName && (
          <Box component="span">
            <FontAwesomeIcon
              icon={faIndustry}
              style={{ color: theme.palette.text.secondary, marginRight: 2 }}
            />
            <Box component="span" sx={{ color: 'primary.main' }}>
              {formik.values.vendorName}
            </Box>
          </Box>
        )}
      </>
    );
    switch (mode) {
      case PurchaseOrderModalMode.EDIT:
        return (
          <>
            {tAddO('Edit Purchase Order') + ' '}
            {titleInfo}
          </>
        );
      case PurchaseOrderModalMode.VIEW:
        return (
          <>
            {tAddO('View Purchase Order') + ' '}
            {titleInfo}
          </>
        );
      case PurchaseOrderModalMode.CREATE:
        return (
          <>
            {tAddO('Add Purchase Order') + ' '}
            {titleInfo}
          </>
        );
      default:
        return '';
    }
  };

  // This functions set default values
  useEffect(() => {
    if (purchaseOrderIdToEdit) return; // If we are editing, do not set default values
    if (paymentTerms.length > 0) {
      const defaultTerm = paymentTerms.find((pT) => pT.default);
      if (defaultTerm) {
        formik.setFieldValue('paymentTerms', [defaultTerm]);
      }
    }
    if (shipMethods.length > 0) {
      const defaultShipMethods = shipMethods.find((sM) => sM.default);
      if (defaultShipMethods) {
        formik.setFieldValue('shipMethod', defaultShipMethods.ShipMethodId);
      }
    }
    if (purchaseOrderflowSettings != null) {
      const purchaseOrderD = formik.values.orderDate;
      const sD = purchaseOrderflowSettings.purchaseOrders.shippingDays;
      const tD = purchaseOrderflowSettings.purchaseOrders.trackingDays;
      if (purchaseOrderD && sD && tD && sD > 0 && tD > 0) {
        formik.setFieldValue(
          'estimatedShipDate',
          dayjs.unix(purchaseOrderD).add(sD, 'day').unix(),
        );
        formik.setFieldValue(
          'deadline',
          dayjs
            .unix(purchaseOrderD)
            .add(sD + tD, 'day')
            .unix(),
        );
      }
    }
  }, [paymentTerms, shipMethods, purchaseOrderflowSettings]); // Only run when `paymentTerms`, `shipMethods` or `purchaseOrderflowSettings` changes

  // This just gets what tab has errors
  const tabsWithError = getTabsWithVisibleErrors(
    TAB_ERROR_PREFIXES,
    formik.errors,
    formik.touched,
  );

  return (
    <Dialog
      open={isOpenPurchaseOrderModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="xl"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {renderTitle()}
      </DialogTitle>
      <IconButton
        disabled={loading || disableButtons}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-around',
                  },
                }}
                TabIndicatorProps={{
                  sx: tabIndicatorErrorSx(tabHasError(tab, tabsWithError)),
                }}
              >
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(0, tabsWithError)),
                  ]}
                  label={tAddO('PURCHASE ORDER DETAILS')}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(1, tabsWithError)),
                  ]}
                  label={tAddO('ADD ITEM')}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <PurchaseOrderDetails
                formik={formik}
                paymentTerms={paymentTerms}
                shipMethods={shipMethods}
                viewMode={mode == PurchaseOrderModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <Items
                formik={formik}
                viewMode={mode == PurchaseOrderModalMode.VIEW}
              />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading || disableButtons}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {mode == PurchaseOrderModalMode.VIEW ? (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                exitViewMode();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Edit')
              )}
            </Button>
          ) : (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Save')
              )}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
